/**
 * GA4 / Firebase Analytics for web.
 * Consent-gated: no events are sent until user has accepted cookies (preferences.cookies === true).
 * Requires VITE_FIREBASE_* env vars and VITE_FIREBASE_MEASUREMENT_ID for analytics.
 */

const PLATFORM = 'web';

type AnalyticsInstance = import('firebase/analytics').Analytics;

let analyticsInstance: AnalyticsInstance | null = null;
let consentGranted = false;
let userIdentity: { userId: string; orgId?: string; role?: string } | null = null;
let logEventFn: ((analytics: AnalyticsInstance, name: string, params?: object) => void) | null = null;
let setUserIdFn: ((analytics: AnalyticsInstance, id: string | null) => void) | null = null;

function hasFirebaseAnalyticsConfig(): boolean {
  const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;
  const hasBase =
    import.meta.env.VITE_FIREBASE_API_KEY &&
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN &&
    import.meta.env.VITE_FIREBASE_PROJECT_ID &&
    import.meta.env.VITE_FIREBASE_APP_ID;
  return Boolean(hasBase && measurementId && String(measurementId).length > 0);
}

/**
 * Initialize analytics only when consent is granted. Idempotent.
 * When consent is false, analytics is not initialized and all track* calls are no-ops.
 */
export async function initAnalyticsIfConsented(consent: boolean): Promise<void> {
  consentGranted = consent;
  if (!consent) {
    return;
  }
  if (!hasFirebaseAnalyticsConfig()) {
    return;
  }
  if (analyticsInstance) {
    return;
  }
  try {
    const { getApps, initializeApp } = await import('firebase/app');
    const analyticsMod = await import('firebase/analytics');
    const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;
    const config = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
      measurementId,
    };
    const app = getApps().length === 0 ? initializeApp(config) : getApps()[0];
    analyticsInstance = analyticsMod.getAnalytics(app);
    logEventFn = analyticsMod.logEvent;
    setUserIdFn = analyticsMod.setUserId;
  } catch {
    // Firebase not configured or analytics not available; no-op
  }
}

/**
 * Track a screen view (SPA route change). No-op if analytics not initialized or consent not granted.
 */
export function trackScreenView(path: string, routeName?: string): void {
  if (!consentGranted || !analyticsInstance || !logEventFn) return;
  try {
    logEventFn(analyticsInstance, 'screen_view', {
      page_path: path,
      page_title: routeName ?? path,
      platform: PLATFORM,
      ...(userIdentity?.orgId && { organization_id: userIdentity.orgId }),
      ...(userIdentity?.role && { user_role: userIdentity.role }),
    });
  } catch {
    // no-op
  }
}

/**
 * Track a custom event. No-op if analytics not initialized or consent not granted.
 * Params should use snake_case; values string or number for GA4.
 */
export function trackEvent(
  name: string,
  params?: Record<string, string | number | boolean>
): void {
  if (!consentGranted || !analyticsInstance || !logEventFn) return;
  try {
    const base: Record<string, string | number | boolean> = {
      platform: PLATFORM,
      ...(userIdentity?.orgId && { organization_id: userIdentity.orgId }),
      ...(userIdentity?.role && { user_role: userIdentity.role }),
    };
    logEventFn(analyticsInstance, name, { ...base, ...params });
  } catch {
    // no-op
  }
}

/**
 * Set user identity for subsequent events (userId required; orgId and role optional).
 */
export function setUserIdentity(
  userId: string,
  orgId?: string,
  role?: string
): void {
  userIdentity = { userId, orgId, role };
  if (!consentGranted || !analyticsInstance || !setUserIdFn) return;
  try {
    setUserIdFn(analyticsInstance, userId);
  } catch {
    // no-op
  }
}

/**
 * Clear user identity (e.g. on logout).
 */
export function clearUserIdentity(): void {
  userIdentity = null;
  if (!analyticsInstance || !setUserIdFn) return;
  try {
    setUserIdFn(analyticsInstance, null);
  } catch {
    // no-op
  }
}

/**
 * Whether analytics is currently enabled (consent + initialized).
 */
export function isAnalyticsEnabled(): boolean {
  return consentGranted && analyticsInstance != null;
}

/** Map SPA path to screen name for GA4 screen_view. */
export function getRouteName(path: string): string {
  if (path === '/' || path === '') return 'Landing';
  if (path === '/login') return 'Login';
  if (path === '/register') return 'Register';
  if (path.startsWith('/dashboard')) return 'Dashboard';
  if (path === '/projects') return 'Projects';
  if (path.startsWith('/project/')) return 'Project Details';
  if (path.startsWith('/tickets')) return 'Tickets';
  if (path === '/calendar') return 'Calendar';
  if (path === '/bauzeitenplan') return 'Bauzeitenplan';
  if (path === '/listen') return 'Listen';
  if (path === '/reports') return 'Reports';
  if (path === '/contractors') return 'Contractors';
  if (path.startsWith('/settings')) return 'Settings';
  if (path === '/notifications') return 'Notifications';
  if (path.startsWith('/invite/accept')) return 'Invite Accept';
  if (path.startsWith('/ticket/')) return 'Public Ticket';
  if (path === '/privacy') return 'Privacy';
  if (path === '/terms') return 'Terms';
  if (path === '/faq') return 'FAQ';
  if (path.startsWith('/forgot-password')) return 'Forgot Password';
  if (path.startsWith('/reset-password')) return 'Reset Password';
  if (path.startsWith('/verify-email')) return 'Verify Email';
  return path || 'Unknown';
}
